/**
 * Pristine profile/auth API client
 * ------------------------------------------------------------------
 * One client for every storefront surface that talks to the custom profile
 * app, so the theme has a single place where the base URL, the credential
 * mode and the error envelope are decided.
 *
 * Why one client: the theme previously carried two hand-rolled fetch wrappers
 * (OTP and saved addresses) that had drifted apart — different paths,
 * different payload casing, different error handling — and two more surfaces
 * (edit profile, preferences) that submitted nothing at all. Any of those is a
 * separate thing to fix the day the app goes live. There is now one.
 *
 * Base URL
 *   Read from `window.PF_API_BASE`, printed once by snippets/pf-api-config
 *   from the theme setting. It is deliberately the ONLY thing that has to
 *   change if the app is reached a different way:
 *
 *     "/apps/pf"                              App Proxy, same-origin
 *     "https://profile-app.example.com"       the app's own origin (CORS)
 *
 *   Either way requests are sent with credentials, because the app
 *   authenticates with a session cookie: same-origin needs `same-origin` at
 *   minimum, cross-origin needs `include`, and `include` is correct for both.
 *
 * Endpoints (the app's contract — paths and payloads are its, not ours)
 *   POST   <base>/v1/auth/phone/start    { phone }         -> { ok, resendAfterSec }
 *   POST   <base>/v1/auth/phone/verify   { phone, code }   -> { ok, customer } + session cookie
 *   POST   <base>/v1/auth/logout                           -> { ok }
 *   GET    <base>/v1/auth/session                          -> { authenticated, customer? }
 *   GET    <base>/v1/me                                    -> { profile }
 *   PATCH  <base>/v1/me                  { firstName, lastName, dob, gender }
 *   GET    <base>/v1/me/addresses                          -> { addresses } (landmark split out of address2)
 *   POST   <base>/v1/me/addresses        { ...address }    -> { addresses, createdId }
 *   PATCH  <base>/v1/me/addresses/:id    { ...address }
 *   DELETE <base>/v1/me/addresses/:id
 *   POST   <base>/v1/me/addresses/:id/default
 *   GET    <base>/v1/me/orders[?after]                     -> { orders, pageInfo }
 *   GET    <base>/v1/me/orders/:id                         -> { order }
 *   GET    <base>/v1/me/preferences                        -> { preferences }
 *   PUT    <base>/v1/me/preferences      { skinType, skinConcerns, fragrancePreference }
 *
 * Errors
 *   The app answers `{ error: { code, message, fields?, requestId } }`. A
 *   rejected promise therefore carries `.code`, `.fields` and `.status` so a
 *   form can put the message on the field that caused it instead of showing
 *   one generic line. Anything that is not the app answering — no base set,
 *   an unrouted path, HTML instead of JSON, a network failure — collapses to
 *   code NOT_CONNECTED, which is a different thing from a request the app
 *   refused and must not be reported as "try again".
 */
(function () {
  'use strict';
  if (window.PFApi) return;

  var NOT_CONNECTED_MESSAGE =
    'We could not connect to your profile. Please try again.';

  function PFApiError(code, message, status, fields, retryAfterSec) {
    var error = new Error(message);
    error.name = 'PFApiError';
    error.code = code;
    error.status = status || 0;
    error.fields = fields || null;
    // The app rate-limits with a Retry-After header rather than a body field,
    // so a caller that wants to run a cooldown reads it from here.
    error.retryAfterSec = retryAfterSec || 0;
    error.notConnected = code === 'NOT_CONNECTED';
    return error;
  }

  function base() {
    var value = window.PF_API_BASE;
    if (typeof value !== 'string') return '';
    return value.trim().replace(/\/+$/, '');
  }

  function urlFor(path) {
    var root = base();
    if (!root) return null;
    return root + (path.charAt(0) === '/' ? path : '/' + path);
  }

  var bridgeReady;
  var bridgeStorageKey = 'pf:session:bridge';
  var bridgeAttemptKey = 'pf:session:bridge-attempt';
  function resetSessionBridge() { try { sessionStorage.removeItem(bridgeAttemptKey); } catch (_) {} }
  function contactReturn() {
    try {
      var saved = JSON.parse(sessionStorage.getItem('pf:shared:nonce') || 'null');
      if (saved && Date.now() - saved.at < 840000) return { nonce: saved.value, returnTo: location.href };
    } catch (_) {}
    return {};
  }
  async function resumeSession(force) {
    if (!base() || !window.crypto || !crypto.subtle) return false;
    try {
      var attempted = Number(sessionStorage.getItem(bridgeAttemptKey) || 0);
      if (!force && Date.now() - attempted < 60000) return false;
      var bytes = crypto.getRandomValues(new Uint8Array(32));
      var encode = function (value) { return btoa(String.fromCharCode.apply(null, value)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); };
      var verifier = encode(bytes);
      var challenge = encode(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
      sessionStorage.setItem(bridgeStorageKey, verifier);
      sessionStorage.setItem(bridgeAttemptKey, String(Date.now()));
      var target = new URL(location.href); target.hash = '';
      var bridge = new URL(urlFor('/v1/auth/bridge'), location.origin);
      bridge.searchParams.set('return_to', target.toString()); bridge.searchParams.set('challenge', challenge);
      // Wait for the document, not third-party pixels/images that may never load.
      if (document.readyState === 'loading') await new Promise(function (resolve) { document.addEventListener('DOMContentLoaded', resolve, { once: true }); });
      await new Promise(function (resolve) { setTimeout(resolve, 0); });
      location.replace(bridge.toString()); return true;
    } catch (_) { return false; }
  }
  function syncSharedSession(destination) {
    try {
      var target = new URL(destination || location.href, location.origin);
      var auth = document.querySelector('[data-pf-auth-rby-origin]');
      var rbyOrigin = auth && auth.getAttribute('data-pf-auth-rby-origin');
      if (target.origin !== location.origin && (!rbyOrigin || target.origin !== new URL(rbyOrigin).origin)) throw new Error('Invalid sign-in destination');
      sessionStorage.setItem('pf:shared:return', target.toString());
      var back = new URL(location.href); back.hash = '';
      var start = new URL(urlFor('/v1/auth/bridge/shared'), location.origin); start.searchParams.set('return_to', back.toString());
      location.assign(start.toString()); return true;
    } catch (_) { return false; }
  }
  function sessionBridgeReady() {
    if (bridgeReady) return bridgeReady;
    bridgeReady = (async function () {
      var fragment = new URLSearchParams(location.hash.slice(1));
      var code = fragment.get('pf_bridge');
      var nonce = fragment.get('pf_nonce');
      if (nonce && /^[A-Za-z0-9_-]{43}$/.test(nonce)) sessionStorage.setItem('pf:shared:nonce', JSON.stringify({ value: nonce, at: Date.now() }));
      var refresh = fragment.has('pf_session_refresh');
      var shareNonce = fragment.get('pf_share_nonce'), shared = fragment.has('pf_session_shared');
      if (code || refresh || shareNonce || shared || fragment.has('pf_bridge_failed')) history.replaceState(null, '', location.pathname + location.search);
      if (shareNonce) {
        var response = await request('/v1/auth/bridge/shared', { method: 'POST', body: { nonce: shareNonce, returnTo: location.href }, _bridgeReady: true });
        location.replace(response.url); return new Promise(function () {});
      }
      if (shared) {
        var finalTarget = sessionStorage.getItem('pf:shared:return'); sessionStorage.removeItem('pf:shared:return');
        if (finalTarget && finalTarget !== location.href) { location.replace(finalTarget); return new Promise(function () {}); }
        if (await resumeSession(true)) return new Promise(function () {});
      }
      if (refresh) {
        if (await resumeSession(true)) return new Promise(function () {});
      }
      if (!code) return;
      var verifier = sessionStorage.getItem(bridgeStorageKey); sessionStorage.removeItem(bridgeStorageKey);
      if (!verifier) throw PFApiError('UNAUTHENTICATED', 'Please sign in again to finish connecting your session.', 401);
      await request('/v1/auth/bridge', { method: 'POST', body: { code: code, verifier: verifier }, _bridgeReady: true });
    })();
    return bridgeReady;
  }

  /**
   * `credentials: 'include'` is what carries the app's session cookie. On the
   * App Proxy the request is same-origin and the cookie is first-party; on the
   * app's own origin the app must answer with an exact
   * Access-Control-Allow-Origin and Allow-Credentials, which it does for the
   * origins on its allowlist.
   */
  function request(path, options) {
    var opts = options || {};
    if (!opts._bridgeReady) return sessionBridgeReady().then(function () { return request(path, Object.assign({}, opts, { _bridgeReady: true })); });
    var url = urlFor(path);

    if (!url) {
      return Promise.reject(
        PFApiError('NOT_CONNECTED', NOT_CONNECTED_MESSAGE, 0)
      );
    }

    if (opts.query) {
      var parts = [];
      Object.keys(opts.query).forEach(function (key) {
        var value = opts.query[key];
        if (value === undefined || value === null || value === '') return;
        parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
      });
      if (parts.length) url += (url.indexOf('?') === -1 ? '?' : '&') + parts.join('&');
    }

    var headers = { Accept: opts.responseType === 'blob' ? 'application/pdf' : 'application/json' };
    if (opts.body !== undefined && !opts.form) headers['Content-Type'] = 'application/json';

    return window
      .fetch(url, {
        method: opts.method || 'GET',
        credentials: 'include',
        headers: headers,
        body: opts.form || (opts.body === undefined ? undefined : JSON.stringify(opts.body))
      })
      .catch(function () {
        // DNS, TLS, blocked by CORS, offline — never the app refusing.
        throw PFApiError('NOT_CONNECTED', NOT_CONNECTED_MESSAGE, 0);
      })
      .then(function (response) {
        if (response.ok && opts.responseType === 'blob') return response.blob();
        return response
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            /* An unrouted path is not an API error: with no app behind it
               Shopify serves the storefront 404 page, so the status is 404 and
               the body is HTML. Reporting that as "please try again" sends the
               shopper round a loop that cannot succeed. */
            if (body === null) {
              throw PFApiError(
                'NOT_CONNECTED',
                NOT_CONNECTED_MESSAGE,
                response.status
              );
            }

            if (!response.ok) {
              var envelope = body.error || {};
              throw PFApiError(
                envelope.code || 'HTTP_' + response.status,
                envelope.message || 'Something went wrong. Please try again.',
                response.status,
                envelope.fields || null,
                parseInt(response.headers.get('Retry-After'), 10) || 0
              );
            }

            return body;
          });
      });
  }

  function shorthand(method) {
    return function (path, body, options) {
      var opts = options || {};
      opts.method = method;
      if (body !== undefined) opts.body = body;
      return request(path, opts);
    };
  }

  window.PFApi = {
    contactReturn: contactReturn,
    resumeSession: resumeSession,
    syncSharedSession: syncSharedSession,
    resetSessionBridge: resetSessionBridge,
    isConfigured: function () {
      return !!base();
    },
    base: base,
    request: request,
    get: function (path, options) {
      return request(path, options || {});
    },
    post: shorthand('POST'),
    patch: shorthand('PATCH'),
    put: shorthand('PUT'),
    del: shorthand('DELETE'),
    error: PFApiError,
    NOT_CONNECTED_MESSAGE: NOT_CONNECTED_MESSAGE,

    /**
     * Shopify's address has two street lines and no landmark field, so line 2
     * carries it as "<line 2>, Near <landmark>". The app splits the two apart
     * on every read; anything handing an address straight to Shopify (the
     * /checkout URL) has to put them back together first. Mirrors
     * composeAddress2 in app/services/profile.server.ts.
     */
    composeAddress2: function (address2, landmark) {
      var line = (address2 || '').trim();
      var near = (landmark || '').trim();
      if (!near) return line;
      return line ? line + ', Near ' + near : 'Near ' + near;
    },

    /**
     * Puts a validation failure on the fields it names. The app keys `fields`
     * by its own names (fullName, address1, zip, skinType…), so a form opts in
     * by tagging inputs with data-pf-field="<that name>".
     */
    applyFieldErrors: function (root, fields) {
      if (!root) return 0;
      var applied = 0;
      var aliases = {
        fullName: ['firstName', 'lastName'],
        provinceCode: ['province', 'state'],
        countryCode: ['country']
      };
      function messageFor(key) {
        if (!fields) return null;
        if (fields[key]) return fields[key];
        var candidates = [key].concat(aliases[key] || []);
        var fieldKeys = Object.keys(fields);
        for (var i = 0; i < fieldKeys.length; i += 1) {
          var leaf = fieldKeys[i].split('.').pop();
          if (candidates.indexOf(leaf) !== -1) return fields[fieldKeys[i]];
        }
        return null;
      }
      root.querySelectorAll('[data-pf-field]').forEach(function (node) {
        var key = node.getAttribute('data-pf-field');
        var message = messageFor(key);
        node.classList.toggle('is-invalid', !!message);
        if (message) {
          node.setAttribute('aria-invalid', 'true');
          applied += 1;
        } else {
          node.removeAttribute('aria-invalid');
        }
        var slot = root.querySelector('[data-pf-field-error="' + key + '"]');
        if (slot) {
          slot.textContent = message || '';
          slot.hidden = !message;
        }
      });
      return applied;
    }
  };
  if (/^#pf_(?:bridge|session|share)/.test(location.hash)) sessionBridgeReady().catch(function (error) {
    var slot = document.querySelector('[data-pf-auth-status], [data-pf-gate-error]');
    if (slot) { slot.textContent = error.message; slot.hidden = false; }
  });
})();
